import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { spacing } from '../../design-system/tokens';
import { Button } from '../../design-system/components/inputs/Button';
import { ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/outline';
import { userService } from '../../services';
import { projectsService } from '../../services/projectsService';
import { navigationService } from '../../services/navigationService';
import { useProject } from '../../contexts/ProjectContext';

// Step Components
import { AccountSetup, EmailConnect } from './steps';

interface OnboardingFlowProps {
  onComplete: () => void;
}

type OnboardingStep = 'account' | 'email-connect';

interface OnboardingData {
  account: {
    organizationName: string;
    userName: string;
  } | null;
  emailConnected: boolean;
}

const Container = styled.div`
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[8]} ${spacing[8]};
  background: #ffffff;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconImage = styled.img`
  height: 28px;
  width: 28px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
`;


const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const ProgressDot = styled.div<{ active: boolean; completed: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => 
    props.completed ? '#635bff' :
    props.active ? '#635bff' : '#e2e8f0'
  };
  transition: all 0.15s ease;
  opacity: ${props => props.active || props.completed ? 1 : 0.4};
`;

const Content = styled.div<{ splitScreen?: boolean }>`
  flex: 1;
  background: #ffffff;
  ${props => props.splitScreen ? `
    min-height: 600px;
  ` : `
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: ${spacing[10]} ${spacing[8]} ${spacing[8]} ${spacing[8]};
  `}
`;

const SimpleContent = styled.div`
  max-width: 480px;
  width: 100%;
`;

const Navigation = styled.div<{ splitScreen?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing[6]} ${spacing[8]};
  background: #ffffff;
`;

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const { loadProjects } = useProject();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('account');
  const [data, setData] = useState<OnboardingData>({
    account: null,
    emailConnected: false
  });
  const [loading, setLoading] = useState(false);

  // Define steps
  const steps = ['account', 'email-connect'] as OnboardingStep[];
  const currentStepIndex = steps.indexOf(currentStep);

  // Navigation logic
  const canProceed = () => {
    switch (currentStep) {
      case 'account':
        return !!data.account?.organizationName && !!data.account?.userName;
      case 'email-connect':
        return true; // Can always skip
      default:
        return false;
    }
  };

  // Smart step navigation - skip irrelevant steps
  const getNextStep = (currentStep: OnboardingStep): OnboardingStep | null => {
    switch (currentStep) {
      case 'account':
        return 'email-connect';
      case 'email-connect':
        return null; // End of flow - go to dashboard
      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (!canProceed()) return;

    setLoading(true);
    try {
      // Save data when leaving account step
      if (currentStep === 'account' && data.account) {
        await userService.setupOrganization(data.account.organizationName);
        userService.clearCache();
      }

      // Smart navigation to next relevant step or complete onboarding
      const nextStep = getNextStep(currentStep);
      if (nextStep) {
        setCurrentStep(nextStep);
      } else {
        // No more steps - create first project and complete onboarding
        console.log('[Onboarding] Completing with data:', {
          organization: data.account?.organizationName,
          emailConnected: data.emailConnected
        });

        try {
          // Create the user's default first project
          console.log('[Onboarding] Creating default first project: My First Project');
          setLoading(true); // Show loading state
          
          const newProject = await projectsService.createProject({
            name: "My First Project",
            description: ""
          });
          
          console.log('[Onboarding] First project created successfully:', newProject);
          
          // Wait for backend to fully sync the new project
          console.log('[Onboarding] Waiting 3 seconds for backend sync...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Now reload projects to get the newly created project into context
          console.log('[Onboarding] Reloading projects to include new project...');
          await loadProjects();
          
          // Complete onboarding (this will trigger OnboardingGuard cleanup)
          console.log('[Onboarding] Completing onboarding...');
          onComplete();
          
          // Small delay to ensure onboarding guard cleanup is complete
          console.log('[Onboarding] Waiting for onboarding cleanup...');
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Now redirect to the new project
          console.log('[Onboarding] Redirecting to new project...');
          navigate(navigationService.project(newProject.project_uuid));
          return;
        } catch (error) {
          console.error('[Onboarding] Error creating first project:', error);
          // Continue with onboarding completion even if project creation fails
        }

        onComplete();
        return;
      }
    } catch (error) {
      console.error('Error proceeding:', error);
    } finally {
      setLoading(false);
    }
  };

  // Smart back navigation - go to previous relevant step
  const getPreviousStep = (currentStep: OnboardingStep): OnboardingStep | null => {
    switch (currentStep) {
      case 'email-connect':
        return 'account';
      default:
        return null;
    }
  };

  const handleBack = () => {
    const previousStep = getPreviousStep(currentStep);
    if (previousStep) {
      setCurrentStep(previousStep);
    }
  };

  // Data handlers
  const handleAccountData = useCallback((accountData: { organizationName: string; userName: string }) => {
    console.log('Account data updated:', accountData);
    setData(prev => ({ ...prev, account: accountData }));
  }, []);

  const handleEmailConnectionChange = useCallback((connected: boolean) => {
    console.log('Email connection changed:', connected);
    setData(prev => ({ ...prev, emailConnected: connected }));
  }, []);


  const getButtonText = () => {
    if (loading && currentStep === 'email-connect') {
      return 'Setting up your workspace...';
    }
    
    switch (currentStep) {
      case 'account': return 'Continue';
      case 'email-connect': return 'Create project & enter dashboard';
      default: return 'Continue';
    }
  };

  // Debug logging for data changes
  useEffect(() => {
    console.log('[Onboarding] Data updated:', {
      account: data.account,
      emailConnected: data.emailConnected
    });
  }, [data]);

  const isSplitScreen = false; // All steps are now simple single-column layouts

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'account':
        return (
          <AccountSetup 
            data={data.account}
            onDataChange={handleAccountData}
          />
        );
      case 'email-connect':
        return (
          <EmailConnect 
            onConnectionChange={handleEmailConnectionChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Header>
        <LogoSection>
          <IconImage src="/images/adly-icon.png" alt="Adly Icon" />
        </LogoSection>
        <Progress>
          {steps.map((step, index) => (
            <ProgressDot
              key={step}
              active={index === currentStepIndex}
              completed={index < currentStepIndex}
            />
          ))}
        </Progress>
      </Header>

      <Content splitScreen={isSplitScreen}>
        {isSplitScreen ? (
          renderCurrentStep()
        ) : (
          <SimpleContent>
            {renderCurrentStep()}
          </SimpleContent>
        )}
      </Content>

              <Navigation splitScreen={isSplitScreen}>
        <div>
          {currentStepIndex > 0 && (
            <Button
              variant="ghost"
              size="sm"
              icon={<ArrowLeftIcon />}
              onClick={handleBack}
              style={{
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '400'
              }}
            >
              Back
            </Button>
          )}
        </div>

        <div>
          <Button
            variant="primary"
            size="base"
            icon={<ArrowRightIcon />}
            onClick={handleNext}
            disabled={!canProceed()}
            loading={loading}
            style={{
              background: '#635bff',
              borderColor: '#635bff',
              fontWeight: '500',
              fontSize: '15px',
              minWidth: '140px'
            }}
          >
            {getButtonText()}
          </Button>
        </div>
      </Navigation>
    </Container>
  );
};